(() => {
  const STORAGE_KEY = 'friziHomepagePackContextV1';
  const TTL_MS = 24 * 60 * 60 * 1000;
  const CHANGE_EVENT = 'frizi:pack-context-change';

  const parse = (storage) => {
    try {
      const raw = storage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  };

  const normalize = (candidate, fallbackUpdatedAt = 0) => {
    const requiredPosters = Number(candidate?.requiredPosters || 0);
    if (!candidate || candidate.version !== 1 || !candidate.key || !requiredPosters) return null;

    const selectedPosters = Array.isArray(candidate.selectedPosters)
      ? candidate.selectedPosters.map((poster) => ({
          id: String(poster?.id || ''),
          handle: String(poster?.handle || ''),
          title: String(poster?.title || ''),
          variantId: String(poster?.variantId || ''),
          image: String(poster?.image || '')
        })).filter((poster) => poster.id && poster.handle).slice(0, requiredPosters)
      : [];

    return {
      version: 1,
      key: String(candidate.key),
      title: String(candidate.title || ''),
      requiredPosters,
      variantId: String(candidate.variantId || ''),
      requiredTag: String(candidate.requiredTag || ''),
      shopUrl: String(candidate.shopUrl || ''),
      composerUrl: String(candidate.composerUrl || ''),
      resumeTarget: String(candidate.resumeTarget || ''),
      selectedPosters,
      updatedAt: Number(candidate.updatedAt || fallbackUpdatedAt || 0)
    };
  };

  const isExpired = (context) => !context?.updatedAt || Date.now() - context.updatedAt > TTL_MS;
  const sameMeaningfulState = (left, right) => JSON.stringify({
    key: left.key,
    requiredPosters: left.requiredPosters,
    variantId: left.variantId,
    requiredTag: left.requiredTag,
    selectedPosters: left.selectedPosters
  }) === JSON.stringify({
    key: right.key,
    requiredPosters: right.requiredPosters,
    variantId: right.variantId,
    requiredTag: right.requiredTag,
    selectedPosters: right.selectedPosters
  });

  const emit = (context) => window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { context } }));

  const remove = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('[frizi-pack-context] suppression indisponible', error);
    }
    emit(null);
  };

  const readLocal = () => {
    const context = normalize(parse(localStorage));
    if (!context) return null;
    if (isExpired(context)) {
      remove();
      return null;
    }
    return context;
  };

  const migrateSessionContext = () => {
    const localContext = readLocal();
    if (localContext) {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (error) { /* noop */ }
      return localContext;
    }

    const legacyContext = normalize(parse(sessionStorage), Date.now());
    if (!legacyContext) return null;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(legacyContext));
      sessionStorage.removeItem(STORAGE_KEY);
      return legacyContext;
    } catch (error) {
      console.warn('[frizi-pack-context] migration session indisponible', error);
      return null;
    }
  };

  const read = () => readLocal() || migrateSessionContext();

  const write = (candidate) => {
    const previous = readLocal();
    const next = normalize(candidate, previous?.updatedAt || Date.now());
    if (!next) return null;

    next.updatedAt = !previous || !sameMeaningfulState(previous, next)
      ? Date.now()
      : previous.updatedAt;

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      sessionStorage.removeItem(STORAGE_KEY);
      emit(next);
      return next;
    } catch (error) {
      console.warn('[frizi-pack-context] sauvegarde indisponible', error);
      return null;
    }
  };

  window.FriziPackContext = {
    key: STORAGE_KEY,
    ttlMs: TTL_MS,
    changeEvent: CHANGE_EVENT,
    read,
    write,
    clear: remove
  };

  migrateSessionContext();
})();
